import { Controller } from "@hotwired/stimulus"
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  RadarController,
  RadialLinearScale,
  Tooltip
} from "chart.js"

const EMPTY_STATE_PLUGIN = {
  id: "emptyState",
  afterDraw(chart, _args, options) {
    if (!options?.enabled) return
    if (hasRenderableData(chart.data.datasets)) return

    const { chartArea, ctx } = chart
    if (!chartArea) return

    ctx.save()
    ctx.fillStyle = "#7B8293"
    ctx.font = "500 13px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillText(options.text || "No data yet", (chartArea.left + chartArea.right) / 2, (chartArea.top + chartArea.bottom) / 2)
    ctx.restore()
  }
}

function hasRenderableData(datasets = []) {
  return datasets.some((dataset) =>
    (dataset.data || []).some((value) => {
      const numeric = Number(value)
      return Number.isFinite(numeric) && numeric !== 0
    })
  )
}

Chart.register(
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  DoughnutController,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  RadarController,
  RadialLinearScale,
  Tooltip,
  EMPTY_STATE_PLUGIN
)

const BRAND = {
  primary: "#3B5DBA",
  soft: "#5B85C9",
  good: "#5C7A53",
  warning: "#B86A38",
  danger: "#8B2828",
  text: "#5A6072",
  grid: "rgba(216, 222, 234, 0.72)"
}

export default class extends Controller {
  static values = {
    data: Object,
    kind: { type: String, default: "line" },
    max: { type: Number, default: 100 },
    stacked: { type: Boolean, default: false },
    legend: { type: Boolean, default: false }
  }

  connect() {
    this.chart?.destroy()
    this.chart = new Chart(this.element, this.chartConfig())
  }

  disconnect() {
    this.chart?.destroy()
  }

  chartConfig() {
    if (this.kindValue === "doughnut") return this.doughnutConfig()
    if (this.kindValue === "gauge") return this.gaugeConfig()
    if (this.kindValue === "radar") return this.radarConfig()
    if (this.kindValue === "bar") return this.xyConfig("bar")

    return this.xyConfig("line")
  }

  xyConfig(defaultType) {
    const labels = this.dataValue.labels || []
    const datasets = (this.dataValue.datasets || []).map((dataset, index) => {
      const color = dataset.color || this.palette(index)
      const type = dataset.type || defaultType
      return {
        type,
        label: dataset.label,
        data: dataset.data || [],
        borderColor: color,
        backgroundColor: type === "line" ? this.gradient(color) : `${color}CC`,
        borderWidth: type === "line" ? 2.5 : 0,
        borderRadius: type === "bar" ? 7 : 0,
        fill: dataset.fill ?? type === "line",
        tension: dataset.tension ?? 0.35,
        pointRadius: type === "line" ? 2.5 : 0,
        pointHoverRadius: type === "line" ? 5 : 0,
        pointBackgroundColor: "#FFFFFF",
        pointBorderColor: color,
        pointBorderWidth: 2,
        yAxisID: dataset.yAxisID || "y"
      }
    })

    return {
      type: defaultType,
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          emptyState: { enabled: true, text: this.dataValue.emptyText || "No data yet" },
          legend: {
            display: this.legendValue,
            labels: { color: BRAND.text, boxWidth: 10, boxHeight: 10, usePointStyle: true }
          },
          tooltip: { enabled: true }
        },
        scales: {
          x: {
            stacked: this.stackedValue,
            grid: { display: false },
            ticks: { color: "#9BA1AB", maxTicksLimit: 8 }
          },
          y: {
            stacked: this.stackedValue,
            min: 0,
            max: this.maxValue > 0 ? this.maxValue : undefined,
            grid: { color: BRAND.grid },
            ticks: { color: "#9BA1AB", stepSize: this.maxValue <= 10 ? 2 : undefined }
          }
        }
      }
    }
  }

  doughnutConfig() {
    const labels = this.dataValue.labels || []
    const values = this.dataValue.values || []
    const colors = this.dataValue.colors || labels.map((_, index) => this.palette(index))

    return {
      type: "doughnut",
      data: {
        labels,
        datasets: [{ data: values, backgroundColor: colors, borderColor: "#FFFFFF", borderWidth: 2 }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "68%",
        plugins: {
          emptyState: { enabled: true, text: this.dataValue.emptyText || "No data yet" },
          legend: {
            display: this.legendValue,
            position: "bottom",
            labels: { color: BRAND.text, boxWidth: 10, boxHeight: 10, usePointStyle: true }
          },
          tooltip: { enabled: true }
        }
      }
    }
  }

  gaugeConfig() {
    const score = Math.max(0, Math.min(100, Number(this.dataValue.score || 0)))
    const color = this.dataValue.color || (score >= 75 ? BRAND.good : score >= 55 ? BRAND.warning : BRAND.danger)

    return {
      type: "doughnut",
      data: {
        labels: ["Score", "Remaining"],
        datasets: [{
          data: [score, 100 - score],
          backgroundColor: [color, "rgba(216, 222, 234, 0.65)"],
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "76%",
        circumference: 270,
        rotation: 225,
        plugins: {
          emptyState: { enabled: false },
          legend: { display: false },
          tooltip: { enabled: false }
        }
      }
    }
  }

  radarConfig() {
    const labels = this.dataValue.labels || []
    const datasets = (this.dataValue.datasets || []).map((dataset, index) => {
      const color = dataset.color || this.palette(index)
      return {
        label: dataset.label,
        data: dataset.data || [],
        borderColor: color,
        backgroundColor: `${color}26`,
        pointBackgroundColor: color,
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2,
        borderWidth: 2
      }
    })

    return {
      type: "radar",
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          emptyState: { enabled: true, text: this.dataValue.emptyText || "No data yet" },
          legend: { display: this.legendValue, labels: { color: BRAND.text } },
          tooltip: { enabled: true }
        },
        scales: {
          r: {
            min: 0,
            max: this.maxValue,
            angleLines: { color: BRAND.grid },
            grid: { color: BRAND.grid },
            pointLabels: { color: BRAND.text, font: { size: 11 } },
            ticks: { display: false }
          }
        }
      }
    }
  }

  gradient(color) {
    const context = this.element.getContext("2d")
    const gradient = context.createLinearGradient(0, 0, 0, this.element.clientHeight || 260)
    gradient.addColorStop(0, `${color}35`)
    gradient.addColorStop(1, `${color}00`)
    return gradient
  }

  palette(index) {
    return [BRAND.primary, BRAND.good, BRAND.warning, BRAND.danger, BRAND.soft][index % 5]
  }
}
